import { useEffect, useState, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { purchasingAPI, PurchaseOrder, POChangeLog, PODocument, POInsights } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import CommunicationLog from './CommunicationLog';
import AckUploadHistory from './AckUploadHistory';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import VerticalTimeline from './timeline/VerticalTimeline';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { convertChangelogsToTimeline } from './PODetailHelpers';
import { PrintablePOSummary } from './PrintablePOSummary';
import { formatPartySubtitle } from './partySubtitle';
import { usePODetailFormatters } from './usePODetailFormatters';
import { PODetailOverviewTab } from './PODetailOverviewTab';
import { PODetailLinesTab } from './PODetailLinesTab';
import { PODetailDocumentsTab } from './PODetailDocumentsTab';

interface PODetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  poId: number;
  /** CR-7716: tab to open on (e.g. 'documents' from a PDF/Cert click). Defaults to Overview. */
  initialTab?: string;
}

export default function PODetailModal({ isOpen, onClose, poId, initialTab = 'overview' }: PODetailModalProps) {
  const { user } = useAuth();
  const isSupplierUser = user?.platform === 'SUPPLIER';
  const [po, setPO] = useState<PurchaseOrder | null>(null);
  const [changelogs, setChangelogs] = useState<POChangeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CR-5553 rework: control the active tab so a post-upload refetch doesn't
  // bounce the user back to Overview. The Tabs used `defaultValue="overview"`
  // (uncontrolled), so the brief loading flip during fetchPODetails() unmounted
  // and remounted the Tabs, resetting to Overview after every upload.
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  // Customer review 11 Sep (items 25/26): OTIF %, PPM/K, buyers and the
  // per-line NCR / ASN figures. Loaded beside the order, never blocking it —
  // a failure here leaves the order readable and the figures "Unavailable".
  const [insights, setInsights] = useState<POInsights | null>(null);
  const [insightsState, setInsightsState] = useState<'loading' | 'ready' | 'error'>('loading');

  // The order the latest insights request was for: a slower response for an
  // order the user has already moved away from must not overwrite this one.
  const insightsFor = useRef<number | null>(null);

  const fetchInsights = async () => {
    const requested = poId;
    insightsFor.current = requested;
    setInsights(null);
    setInsightsState('loading');
    try {
      const response = await purchasingAPI.getOrderInsights(requested);
      if (insightsFor.current !== requested) return;
      setInsights(response.data);
      setInsightsState('ready');
    } catch (err: unknown) {
      if (insightsFor.current !== requested) return;
      console.warn('Failed to load PO insights:', err);
      setInsightsState('error');
    }
  };

  useEffect(() => {
    if (isOpen && poId) {
      // Each fresh open starts on Overview, unless the caller asked for a tab
      // (CR-7716: a PDF/Cert click opens straight on Documents).
      setActiveTab(initialTab);
      fetchPODetails();
      fetchInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, poId]);

  // CR-5553 rework: `silent` skips the full-screen loading state so an in-place
  // refresh (e.g. after a document upload) doesn't unmount the tab content.
  const fetchPODetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const [poResponse, changelogResponse] = await Promise.all([
        purchasingAPI.getOrder(poId),
        purchasingAPI.getChangelogs({ po: poId, ordering: '-change_date' }),
      ]);

      setPO(poResponse.data);
      setChangelogs(changelogResponse.data.results);
    } catch (err: unknown) {
      console.error('Failed to fetch PO details:', err);
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load PO details');
    } finally {
      setLoading(false);
    }
  };


  // Still needed here for the print-only summary below.
  const { formatCurrency, formatDate, formatDateTime } = usePODetailFormatters();

  const handleDocumentDownload = async (doc: PODocument) => {
    try {
      const response = await purchasingAPI.downloadPODocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${doc.file_name}`);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error(`Failed to download ${doc.file_name}`);
    }
  };

  // Inline-renderable types only. SVG / HTML / XML are intentionally
  // excluded: a same-origin blob: URL inherits this app's origin, so
  // serving uploader-controlled scriptable content would execute scripts
  // in our security context (stored-XSS via the uploaded MIME type).
  // Anything outside the allowlist is forced to a download via
  // application/octet-stream + the `download` attribute.
  const INLINE_VIEW_MIME_ALLOWLIST = new Set([
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/bmp',
    'image/webp',
    'text/plain',
    'text/csv',
  ]);

  // CR-5553 rework: file extensions matching the inline MIME allowlist above.
  // Used at render time (where the response content-type isn't known yet) to
  // decide whether the "View" button should appear at all.
  const INLINE_VIEW_EXT_ALLOWLIST = new Set([
    'pdf', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'txt', 'csv',
  ]);

  // CR-5553 rework: only documents the browser can genuinely render inline get
  // a "View" button — everything else (doc/docx/xls/xlsx/zip/svg/html/…) shows
  // the Download button only. Previously View force-downloaded non-viewable
  // types, duplicating Download and confusing users. Prefer the stored
  // mime_type; fall back to the file extension when the MIME is missing.
  const isInlineViewable = (doc: PODocument): boolean => {
    const mime = (doc.mime_type || '').toLowerCase().split(';')[0].trim();
    if (mime) return INLINE_VIEW_MIME_ALLOWLIST.has(mime);
    const ext = (doc.file_extension || doc.file_name?.split('.').pop() || '')
      .toLowerCase().replace(/^\./, '');
    return INLINE_VIEW_EXT_ALLOWLIST.has(ext);
  };

  const handleDocumentView = async (doc: PODocument) => {
    // CR-5553 rework: cannot just window.open() the URL.
    // - The serializer's file_url points to /media/po_documents/... which is
    //   hard-blocked by block_private_media in urls.py (CWE-284) → 404.
    // - The download endpoint requires the Authorization header, which a raw
    //   window.open() new tab cannot send.
    // Fetch through the authenticated axios client, then open the response
    // as a same-origin blob URL so the browser renders inline.
    try {
      const response = await purchasingAPI.downloadPODocument(doc.id);
      const headerType = response.headers['content-type'];
      const reportedType = doc.mime_type || (typeof headerType === 'string' ? headerType : '') || '';
      const lower = reportedType.toLowerCase().split(';')[0].trim();
      const isInlineSafe = INLINE_VIEW_MIME_ALLOWLIST.has(lower);
      const blobType = isInlineSafe ? lower : 'application/octet-stream';
      const blob = new Blob([response.data], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      if (isInlineSafe) {
        const opened = window.open(url, '_blank', 'noopener,noreferrer');
        if (!opened) {
          toast.error('Pop-up blocked. Allow pop-ups for this site to view documents.');
        }
      } else {
        // Force a download for anything the browser might render as
        // scriptable content (SVG, HTML, XML, unknown). The user can
        // still inspect the file locally without giving uploader-
        // controlled content our origin.
        const link = window.document.createElement('a');
        link.href = url;
        link.download = doc.file_name || 'document';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      }
      // Release the blob URL after the new tab has had time to load it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('View failed:', err);
      toast.error(`Failed to view ${doc.file_name}`);
    }
  };

  // Customer review 11 Sep (drag and drop everywhere): the Documents tab's
  // FileDropZone owns the input now and hands over the dropped/picked file.
  const handleDocumentFiles = async (files: File[]) => {
    const file = files[0];
    if (!file || !po) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('po', String(po.id));
    formData.append('document_type', 'other');
    formData.append('source', 'supplier');

    try {
      setUploadingDocument(true);
      await purchasingAPI.uploadPODocument(formData);
      toast.success(`Uploaded ${file.name}`);
      // CR-5553 rework: refresh in place (silent) so the user stays on the
      // Documents tab instead of being bounced back to Overview.
      fetchPODetails(true);
    } catch (err) {
      console.error('Upload failed:', err);
      // CR-7460: show the server's reason (it names the accepted types).
      toast.error(`${file.name}: ${getApiErrorMessage(err, 'Failed to upload')}`);
    } finally {
      setUploadingDocument(false);
    }
  };

  if (!isOpen) return null;

  // CR-7258: "PRO0018374 · <customer site> · <supplier>". filter(Boolean)
  // stops a missing party leaving a dangling separator. The PO's customer FK
  // is non-nullable so an absent customer should not reach here, but the
  // Overview card guards the same field with "N/A" and a header is the last
  // place that should be able to render "undefined".
  const poSubtitle = po
    ? formatPartySubtitle(po.po_number, po.customer_name, po.supplier_name)
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold">Purchase Order Details</h2>
            {po && (
              /* CR-7258: name both parties here, not just the supplier. This
                 subtitle is the only part of the modal that survives a tab
                 change -- the Customer Information card lives inside the
                 Overview panel, so on Lines, Documents, Communication and
                 History there was nothing on screen saying whose order it
                 was. Customer first, following the direction of the
                 relationship. Separated with a middle dot rather than the
                 old " - ": customer names are site-qualified and routinely
                 contain a hyphen themselves ("... - TCZEW"), which made a
                 hyphen separator ambiguous about where one party ended. */
              <p
                className="text-sm text-muted-foreground mt-1 truncate"
                title={poSubtitle}
              >
                {poSubtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error: {error}</p>
              <button
                onClick={() => fetchPODetails()}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          ) : po ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="lines">Lines ({po.lines?.length || 0})</TabsTrigger>
                <TabsTrigger value="documents">Documents ({po.documents?.length || 0})</TabsTrigger>
                <TabsTrigger value="communication">Communication</TabsTrigger>
                <TabsTrigger value="history">History ({changelogs.length})</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-6">
                <PODetailOverviewTab po={po} insights={insights} insightsState={insightsState} isSupplierUser={isSupplierUser} />
              </TabsContent>

              {/* Lines Tab */}
              <TabsContent value="lines" className="mt-6">
                <PODetailLinesTab po={po} isSupplierUser={isSupplierUser} insights={insights} insightsState={insightsState} />
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="mt-6">
                <PODetailDocumentsTab
                  po={po}
                  handleDocumentDownload={handleDocumentDownload}
                  handleDocumentView={handleDocumentView}
                  onUploadFiles={handleDocumentFiles}
                  uploading={uploadingDocument}
                  isInlineViewable={isInlineViewable}
                />
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="communication" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Communication</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {po.lines && po.lines.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground mb-4">
                          Select a PO line to view its communication thread.
                        </p>
                        {po.lines.map((line) => (
                          <details key={line.id} className="border rounded-lg">
                            <summary className="p-3 cursor-pointer hover:bg-muted/30 font-medium text-sm">
                              Line {line.line_number} - {line.part_number || 'N/A'} ({line.part_description || 'No description'})
                            </summary>
                            <div className="p-3 border-t">
                              <CommunicationLog
                                lineId={line.id}
                                poId={po.id}
                                partNumber={line.part_number}
                              />
                            </div>
                          </details>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No PO lines available for communication.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="history" className="mt-6 space-y-6">
                {/* CR-6679: Acknowledgement Upload History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Acknowledgement Upload History</CardTitle>
                    <p className="text-sm text-muted-foreground">View uploaded acknowledgement files that processed lines from this PO</p>
                  </CardHeader>
                  <CardContent>
                    <AckUploadHistory poId={poId} />
                  </CardContent>
                </Card>

                {/* Audit History Timeline */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Audit History Timeline</CardTitle>
                    <p className="text-sm text-muted-foreground">Track all changes and events for this purchase order</p>
                  </CardHeader>
                  <CardContent>
                    <VerticalTimeline items={convertChangelogsToTimeline(changelogs)} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
          >
            Close
          </button>
          <button
            onClick={() => window.print()}
            disabled={!po}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Print
          </button>
        </div>
      </div>

      {/* Print-only summary — hidden on screen via `hidden print:block`,
          revealed by @media print in index.css which also hides every
          other DOM surface. window.print() therefore renders just this
          subtree on a clean A4 page. */}
      {po && (
        <PrintablePOSummary
          po={po}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          generatedBy={user?.email || null}
        />
      )}
    </div>
  );
}
